import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, MessageCircle, Send, BadgeCheck, ArrowUpRight, Trash2, Flag, MoreHorizontal, FileText, Download, Share2, Check, X, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";

const fmt = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

function GalleryCell({ src, caption, onClick, className, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative overflow-hidden bg-black group ${className || ""}`}
      data-testid={testId}
    >
      <img src={src} alt={caption || ""} className="w-full h-full object-cover transition group-hover:scale-[1.02]" />
      {caption && (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent px-3 pt-6 pb-2 text-[11px] text-white/95 line-clamp-2 text-left">
          {caption}
        </div>
      )}
    </button>
  );
}

function ImageGallery({ images, captions = [], postId }) {
  const [lightbox, setLightbox] = useState(null); // index or null
  const count = images.length;
  const openAt = (i) => setLightbox(i);
  const close = () => setLightbox(null);
  const step = (dir) => setLightbox((i) => (i == null ? 0 : (i + dir + count) % count));
  const capOf = (i) => (captions && captions[i]) || "";

  let grid = null;
  if (count === 2) {
    grid = (
      <div className="grid grid-cols-2 gap-0.5">
        <GalleryCell src={images[0]} caption={capOf(0)} onClick={() => openAt(0)} className="aspect-square" testId={`post-image-${postId}-0`} />
        <GalleryCell src={images[1]} caption={capOf(1)} onClick={() => openAt(1)} className="aspect-square" testId={`post-image-${postId}-1`} />
      </div>
    );
  } else if (count === 3) {
    grid = (
      <div className="grid grid-cols-2 gap-0.5">
        <GalleryCell src={images[0]} caption={capOf(0)} onClick={() => openAt(0)} className="row-span-2 h-full" testId={`post-image-${postId}-0`} />
        <GalleryCell src={images[1]} caption={capOf(1)} onClick={() => openAt(1)} className="aspect-video" testId={`post-image-${postId}-1`} />
        <GalleryCell src={images[2]} caption={capOf(2)} onClick={() => openAt(2)} className="aspect-video" testId={`post-image-${postId}-2`} />
      </div>
    );
  } else {
    grid = (
      <div className="grid grid-cols-2 gap-0.5">
        <GalleryCell src={images[0]} caption={capOf(0)} onClick={() => openAt(0)} className="aspect-square" testId={`post-image-${postId}-0`} />
        <GalleryCell src={images[1]} caption={capOf(1)} onClick={() => openAt(1)} className="aspect-square" testId={`post-image-${postId}-1`} />
        <GalleryCell src={images[2]} caption={capOf(2)} onClick={() => openAt(2)} className="aspect-square" testId={`post-image-${postId}-2`} />
        <button
          type="button"
          onClick={() => openAt(3)}
          className="relative overflow-hidden bg-black aspect-square"
          data-testid={`post-image-${postId}-3`}
        >
          <img src={images[3]} alt={capOf(3) || ""} className="w-full h-full object-cover" />
          {count > 4 ? (
            <div className="absolute inset-0 bg-black/55 flex items-center justify-center text-white font-semibold text-2xl">
              +{count - 4}
            </div>
          ) : capOf(3) ? (
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent px-3 pt-6 pb-2 text-[11px] text-white/95 line-clamp-2 text-left">
              {capOf(3)}
            </div>
          ) : null}
        </button>
      </div>
    );
  }

  return (
    <>
      {grid}
      <AnimatePresence>
        {lightbox != null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/95 flex items-center justify-center"
            onClick={close}
            data-testid={`post-lightbox-${postId}`}
          >
            <button
              type="button"
              onClick={close}
              className="absolute top-4 right-4 text-white/70 hover:text-white p-2"
              data-testid="lightbox-close"
            >
              <X size={22} />
            </button>
            {count > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); step(-1); }}
                  className="absolute left-4 md:left-8 text-white/70 hover:text-white p-2 rounded-full bg-white/5 hover:bg-white/10"
                  data-testid="lightbox-prev"
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); step(1); }}
                  className="absolute right-4 md:right-8 text-white/70 hover:text-white p-2 rounded-full bg-white/5 hover:bg-white/10"
                  data-testid="lightbox-next"
                >
                  <ChevronRight size={24} />
                </button>
              </>
            )}
            <motion.img
              key={lightbox}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.18 }}
              src={images[lightbox]}
              alt={capOf(lightbox) || ""}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[86vh] max-w-[92vw] object-contain"
            />
            {capOf(lightbox) && (
              <div
                onClick={(e) => e.stopPropagation()}
                className="absolute bottom-14 left-1/2 -translate-x-1/2 max-w-[80vw] text-center text-white/90 text-sm leading-relaxed px-4 py-2 rounded-md bg-black/60 backdrop-blur-sm"
                data-testid={`lightbox-caption-${lightbox}`}
              >
                {capOf(lightbox)}
              </div>
            )}
            {count > 1 && (
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs text-white/60 font-mono">
                {lightbox + 1} / {count}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default function PostCard({ post, onDeleted }) {
  const { user: me, login } = useAuth();
  const [liked, setLiked] = useState(post.liked_by_me);
  const [likeCount, setLikeCount] = useState(post.likes_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [commentCount, setCommentCount] = useState(post.comments_count || 0);

  const requireAuth = () => {
    if (me) return true;
    // Send guest to Google sign-in and bring them back to this exact post page.
    login(`/p/${post.post_id}`);
    return false;
  };

  const handleLike = async () => {
    if (!requireAuth()) return;
    const newLiked = !liked;
    setLiked(newLiked);
    setLikeCount((c) => c + (newLiked ? 1 : -1));
    try {
      const { data } = await api.post(`/posts/${post.post_id}/like`);
      setLikeCount(data.likes_count);
      setLiked(data.liked);
    } catch {
      setLiked(!newLiked);
      setLikeCount((c) => c + (newLiked ? -1 : 1));
    }
  };

  const loadComments = async () => {
    if (showComments) return setShowComments(false);
    setShowComments(true);
    try {
      const { data } = await api.get(`/posts/${post.post_id}/comments`);
      setComments(data);
    } catch {}
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    if (!requireAuth()) return;
    try {
      const { data } = await api.post(`/posts/${post.post_id}/comments`, { text: commentText });
      setComments((c) => [...c, data]);
      setCommentCount((c) => c + 1);
      setCommentText("");
    } catch {}
  };

  const adminDelete = async () => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await api.delete(`/admin/posts/${post.post_id}`);
      onDeleted?.(post.post_id);
    } catch (e) {
      alert("Failed to delete post.");
    }
  };

  const [reported, setReported] = useState(false);
  const report = async () => {
    const reason = window.prompt("Why are you reporting this post? (optional)") ?? null;
    if (reason === null) return; // cancelled
    try {
      await api.post(`/posts/${post.post_id}/report`, { reason });
      setReported(true);
    } catch {
      alert("Failed to report post.");
    }
  };

  const [shared, setShared] = useState(false);
  const share = async () => {
    const url = `${window.location.origin}/api/p/${post.post_id}`;
    const authorName = post.author?.name || "Someone";
    const preview = (post.content || "").trim().slice(0, 140);
    const title = post.post_type === "income_proof" && post.amount
      ? `${authorName} earned $${Number(post.amount).toLocaleString()} on Cashflow`
      : `${authorName} on Cashflow`;
    const text = preview || "Check out this post on Cashflow — the make-money-online social network.";
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
      } else {
        await navigator.clipboard.writeText(url);
        setShared(true);
        setTimeout(() => setShared(false), 1800);
      }
    } catch {
      // user cancelled — no-op
    }
  };

  const a = post.author || {};
  const isProof = post.post_type === "income_proof";

  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`surface rounded-xl p-5 card-hover ${isProof ? "ring-1 ring-[#00E676]/20" : ""}`}
      data-testid={`post-card-${post.post_id}`}
    >
      <header className="flex items-center gap-3 mb-3">
        <Link to={`/profile/${a.user_id}`}>
          <img
            src={a.picture || `https://api.dicebear.com/9.x/notionists/svg?seed=${a.user_id}`}
            alt=""
            className="w-10 h-10 rounded-md object-cover bg-white/5"
            data-testid={`post-author-${post.post_id}`}
          />
        </Link>
        <div className="flex-1 min-w-0">
          <Link
            to={`/profile/${a.user_id}`}
            className="font-semibold text-white hover:text-[#00E676] transition flex items-center gap-1.5 text-sm"
          >
            {a.name || "Anon"}
            {isProof && <BadgeCheck size={14} className="text-[#00E676]" fill="#00E676" stroke="#0A0A0A" strokeWidth={2.5} />}
          </Link>
          <div className="text-[11px] text-white/40 font-mono flex items-center gap-2">
            {post.niche && <span className="px-1.5 py-0.5 rounded bg-white/5 uppercase tracking-wider">{post.niche}</span>}
            <span>{new Date(post.created_at).toLocaleDateString()}</span>
          </div>
        </div>
        {isProof && (
          <div className="verified-pill px-2 py-1 rounded text-[10px] font-mono uppercase tracking-wider flex items-center gap-1">
            <BadgeCheck size={11} strokeWidth={2.5} /> Income Proof
          </div>
        )}
      </header>

      {isProof && (
        <div className="mb-4 -mx-1 p-5 rounded-lg bg-gradient-to-br from-[#00E676]/8 to-transparent border border-[#00E676]/15">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40 mb-1">
            Earned {post.timeframe || ""}
          </div>
          <div className="font-mono text-4xl sm:text-5xl text-[#00E676] tracking-tighter font-semibold" style={{ textShadow: "0 0 24px rgba(0,230,118,0.35)" }}>
            {fmt(post.amount)}
          </div>
        </div>
      )}

      {post.content && <p className="text-white/85 mb-3 leading-relaxed whitespace-pre-wrap text-[15px]">{post.content}</p>}

      {(post.image_urls?.length > 0 || post.image_url || post.proof_image_url || post.video_url || post.document_url) && (
        <div className="rounded-lg overflow-hidden mb-3 border border-white/5 bg-black">
          {post.media_type === "video" || post.video_url ? (
            <video
              src={post.video_url || post.image_url}
              controls
              playsInline
              className="w-full max-h-[600px] bg-black"
              data-testid={`post-video-${post.post_id}`}
            />
          ) : post.media_type === "document" || post.document_url ? (
            <a
              href={post.document_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`post-doc-${post.post_id}`}
              className="flex items-center gap-4 p-4 surface-elev hover:bg-white/[0.06] transition group"
            >
              <div className="w-12 h-12 rounded-md bg-[#00E676]/10 border border-[#00E676]/25 flex items-center justify-center shrink-0">
                <FileText size={20} className="text-[#00E676]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate group-hover:text-[#00E676] transition">
                  {post.document_name || "Document.pdf"}
                </div>
                <div className="text-[11px] font-mono text-white/40">PDF · Click to view</div>
              </div>
              <Download size={16} className="text-white/40 group-hover:text-[#00E676] transition shrink-0" />
            </a>
          ) : post.image_urls?.length > 1 ? (
            <ImageGallery images={post.image_urls} captions={post.image_captions || []} postId={post.post_id} />
          ) : (
            <div className="relative">
              <img
                src={post.proof_image_url || post.image_urls?.[0] || post.image_url}
                alt={(post.image_captions && post.image_captions[0]) || ""}
                className="w-full max-h-[600px] object-cover"
                data-testid={`post-image-${post.post_id}`}
              />
              {post.image_captions && post.image_captions[0] && !post.proof_image_url && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent px-4 pt-8 pb-3 text-sm text-white/95">
                  {post.image_captions[0]}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {post.hashtags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {post.hashtags.map((t) => (
            <span key={t} className="text-xs text-white/45 font-mono">#{t}</span>
          ))}
        </div>
      )}

      <footer className="flex items-center gap-1 pt-3 border-t border-white/5">
        <button
          data-testid={`post-like-btn-${post.post_id}`}
          onClick={handleLike}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition text-sm ${
            liked ? "text-[#00E676]" : "text-white/55 hover:text-white hover:bg-white/5"
          }`}
        >
          <Heart size={16} fill={liked ? "#00E676" : "none"} strokeWidth={2} />
          <span className="font-mono">{likeCount}</span>
        </button>
        <button
          data-testid={`post-comment-btn-${post.post_id}`}
          onClick={loadComments}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-white/55 hover:text-white hover:bg-white/5 transition text-sm"
        >
          <MessageCircle size={16} strokeWidth={2} />
          <span className="font-mono">{commentCount}</span>
        </button>
        {isProof && (
          <Link
            to={`/profile/${a.user_id}`}
            className="flex items-center gap-1 text-xs text-white/40 hover:text-[#00E676] transition font-mono"
          >
            View blueprint <ArrowUpRight size={12} />
          </Link>
        )}
        <button
          data-testid={`share-post-${post.post_id}`}
          onClick={share}
          className={`${isProof ? "" : "ml-auto"} flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs transition ${
            shared ? "text-[#00E676]" : "text-white/45 hover:text-[#00E676] hover:bg-white/5"
          }`}
          title="Share post"
        >
          {shared ? <><Check size={13} strokeWidth={2.5} /> Copied</> : <><Share2 size={13} /> Share</>}
        </button>
        {me?.is_admin && (
          <button
            data-testid={`admin-delete-post-${post.post_id}`}
            onClick={adminDelete}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs text-white/40 hover:text-red-400 hover:bg-red-500/10 transition"
            title="Admin: delete post"
          >
            <Trash2 size={13} />
          </button>
        )}
        {me && me.user_id !== a.user_id && !me.is_admin && (
          <button
            data-testid={`report-post-${post.post_id}`}
            onClick={report}
            disabled={reported}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs transition ${
              reported ? "text-[#F4E8C1] cursor-default" : "text-white/30 hover:text-[#F4E8C1] hover:bg-white/5"
            }`}
            title={reported ? "Reported" : "Report post"}
          >
            <Flag size={12} /> {reported ? "Reported" : ""}
          </button>
        )}
      </footer>

      {showComments && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="mt-3 pt-3 border-t border-white/5 space-y-2"
        >
          {comments.map((c) => (
            <div key={c.comment_id} className="flex gap-2.5" data-testid={`comment-${c.comment_id}`}>
              <img
                src={c.author?.picture || `https://api.dicebear.com/9.x/notionists/svg?seed=${c.author?.user_id}`}
                alt=""
                className="w-7 h-7 rounded-md object-cover bg-white/5"
              />
              <div className="flex-1 surface-elev rounded-lg px-3 py-2">
                <div className="text-xs font-semibold">{c.author?.name}</div>
                <div className="text-sm text-white/70">{c.text}</div>
              </div>
            </div>
          ))}
          <form onSubmit={submitComment} className="flex gap-2 mt-2">
            <input
              data-testid={`comment-input-${post.post_id}`}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onFocus={() => { if (!me) requireAuth(); }}
              placeholder={me ? "Add a comment…" : "Sign in to comment…"}
              className="flex-1 bg-white/[0.04] border border-white/8 rounded-md px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#00E676]/40"
            />
            <button
              data-testid={`comment-submit-${post.post_id}`}
              type="submit"
              className="cta-green rounded-md px-3 py-2 text-sm font-semibold"
            >
              <Send size={14} strokeWidth={2.5} />
            </button>
          </form>
        </motion.div>
      )}
    </motion.article>
  );
}
